import React, { useEffect, useCallback, useRef } from "react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { msalConfig } from "../src/authConfig";
import apiService from "./Components/utils/apiService";
 
// Instantiate MSAL application
const pca = new PublicClientApplication(msalConfig);
 
/**
 * Root MSAL provider wrapping
 */
export function AuthProvider({ children }) {
  return (
    <MsalProvider instance={pca}>
      <InnerAuthHandler>{children}</InnerAuthHandler>
    </MsalProvider>
  );
}
 
/**
 * Handles logout on MSAL errors and specific URLs, consuming messages from authConfig
 */
function InnerAuthHandler({ children }) {
  const { instance } = useMsal();
  const hasLoggedOutRef = useRef(false); // Prevent infinite logout loop
 
  // Security Fix: Enhanced logout to clear application browser storage
  // Reference: WSTG-ATHN-06 - Browser Cache Weakness
  // Note: We clear app-specific data here; full storage clear happens on Login page mount
  //
  // Also revokes all refresh tokens via Microsoft Graph API to prevent new access tokens
  // from being issued after logout. Access tokens are stateless and cannot be revoked,
  // but they expire in ~1 hour.
  const handleLogout = useCallback(async () => {
    if (hasLoggedOutRef.current) return; // Already logged out
    hasLoggedOutRef.current = true;
    console.log("Detected invalid_grant via loggerCallback event, initiating logout");
    
    // Step 1: Try to revoke all refresh tokens via Microsoft Graph API
    // Note: This might fail if the token is already invalid, but we try anyway
    try {
      console.log('Attempting to revoke sign-in sessions before error-based logout...');
      await apiService.revokeSignInSessions();
    } catch (error) {
      // Log but don't block logout - proceed even if revocation fails
      // This is expected when logout is triggered due to token errors
      console.warn('Could not revoke sign-in sessions (may be expected for error-based logout):', error);
    }

    // Step 2: Clear all localStorage and sessionStorage including OID and related items
    // Clear all application data including OID, tokens, and user data
    const appKeys = [
      'directories', 
      'directoriesListDelete', 
      'accessToken', 
      'clientId', 
      'user.role',
      'userOid',      // User Object ID
      'userTid'       // Tenant ID
    ];
    appKeys.forEach(key => localStorage.removeItem(key));
    
    // Clear all localStorage (ensures everything is removed)
    localStorage.clear();
    
    // Clear all sessionStorage
    sessionStorage.clear();
    
    console.log('Application storage cleared, redirecting to Azure logout');
 
    // Logout via MSAL with redirect
    // Step 3: Logout via MSAL with redirect - MSAL will handle its own token cleanup
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  }, [instance]);
  // End Security Fix
 
  // Listen to MSAL log events from loggerCallback
  useEffect(() => {
    const listener = (e) => {
      const { message } = e.detail;
      console.log("MSAL_msg", message); 
      if (e.detail === "Authentication_error") {
        console.log("Detected Authentication_error via loggerCallback event");
        handleLogout();
      }
    };
 
    window.addEventListener("msalLogger", listener);
    return () => window.removeEventListener("msalLogger", listener);
  }, [handleLogout]);
 
  return <>{children}</>;
}
 